import { createFileRoute } from "@tanstack/react-router";
import { CostPageLayout } from "@/components/cost/CostPageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/flat-roof-repair-cost-phoenix";
const TITLE = "Flat Roof Repair Cost in Phoenix (2026) | Durafoam Roofing";
const DESCRIPTION =
  "What flat roof repairs actually cost in Phoenix: pinpoint leak fixes, ponding repairs, full recoats, and when repair stops making sense.";

const faqs = [
  { q: "How much does flat roof repair cost in Phoenix?", a: "Most pinpoint leak repairs run $450–$1,100. Larger ponding repairs and section recoats can land $1,500–$3,500. A full recoat on a typical residential flat roof is $2,800–$4,500." },
  { q: "What's the most common cause of flat roof leaks?", a: "Failed seams, deteriorated coating, and ponding water around HVAC stands or drains. We diagnose the actual source — water rarely shows where it enters." },
  { q: "Repair or recoat?", a: "If the coating is past 80% of its service life and you're getting multiple leaks, a recoat usually makes more sense than chasing repairs. We'll tell you honestly." },
];

export const Route = createFileRoute("/flat-roof-repair-cost-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Flat Roof Repair",
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <CostPageLayout
      pageSlug="flat-roof-repair-cost-phoenix"
      eyebrow="Phoenix cost guide"
      h1="Flat Roof Repair Cost in Phoenix"
      subhead="What flat roof repairs actually cost in 2026 — and when a full recoat is the smarter buy."
      heroBullets={[
        "Pinpoint leak: $450–$1,100",
        "Section repair: $1,200–$2,800",
        "Full recoat: $2,800–$4,500",
        "Free leak diagnosis",
      ]}
      intro={
        <>
          <p>
            Flat roof repair costs in Phoenix depend on what failed and how much of the roof is
            affected. A single failed seam or HVAC penetration is a quick fix. A roof that's
            leaking in multiple places is telling you the coating is at end of life — and at that
            point a recoat usually costs less than chasing the leaks.
          </p>
          <p>
            We diagnose the actual leak source (water rarely enters where it shows up inside),
            photograph everything, and give you a written price before we start.
          </p>
        </>
      }
      pricingRows={[
        { label: "Pinpoint leak repair", range: "$450–$1,100", notes: "Single failure point" },
        { label: "Multi-area repair", range: "$1,200–$2,800", notes: "Several penetrations or seams" },
        { label: "Ponding-area buildout", range: "$800–$2,200", notes: "Add slope to drain" },
        { label: "Full recoat (2,000 sq ft)", range: "$2,800–$4,500", notes: "Two-coat elastomeric" },
        { label: "Section R&R (failed area)", range: "$1,800–$5,000", notes: "Remove & rebuild" },
      ]}
      factors={[
        { title: "Roof type", body: "Foam, mod-bit, BUR, and single-ply all repair differently. We match the repair to the system." },
        { title: "Substrate condition", body: "Wet insulation under the membrane has to be replaced — that's what drives section R&R cost." },
        { title: "Drainage", body: "Adding slope to a ponding area costs more than a simple patch but solves the cause, not the symptom." },
        { title: "Access & obstacles", body: "Roofs loaded with HVAC, solar, and pipe penetrations take longer to inspect and detail." },
      ]}
      faqs={faqs}
      ctaTitle="Get your flat roof diagnosed"
      ctaBody="Use the calculator for a quick estimate, or schedule a free inspection — we'll find the leak source and write you a real quote."
    />
  );
}
